import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Link } from "react-router-dom";

import { NvBadge, nvAgentDetailPath } from "@/components/navigator";
import { NvArrowRightIcon, NvLucideCloseIcon } from "@/components/navigator/nv-icons-mypage";
import { cn } from "@/lib/utils";
import type { FinderCard } from "@/services/navigator/finder-service";
import type { CompareCell, CompareList, CompareMetric } from "@/services/navigator/my-page-service";

import { initialOf } from "./my-agent-card";

// 비교표 팝업 (Figma ver.7 "08 My Page / 비교표 (팝업)" 847:50660 · 제안 03 B안 "전체 펼침 · 정돈형"):
// 1360 폭 · r16 · 머리(제목 + 닫기) · 열 머리행(고정) · 본문 세 묶음(기본 정보 · 도입 조건 · 성과) · 바닥([닫기]).
// 값 목록은 줄마다 하나씩, 같은 행은 가장 긴 칸 높이에 맞추고(grid), 복합 지표는 항목 / 도입 전 → 도입 후로 나눠요. 본문만 스크롤.

type RowKind = "text" | "list" | "metrics";

const GROUPS: { title: string; rows: { key: string; kind: RowKind }[] }[] = [
  {
    title: "기본 정보",
    rows: [
      { key: "agent_type", kind: "text" },
      { key: "deployment", kind: "list" },
      { key: "category", kind: "text" },
    ],
  },
  {
    title: "도입 조건",
    rows: [
      { key: "environment", kind: "list" },
      { key: "pricing", kind: "list" },
      { key: "poc", kind: "text" },
    ],
  },
  { title: "성과", rows: [{ key: "metric", kind: "metrics" }] },
];

// 헤더 로고 타일 글자색 (Figma 예시: 파랑 · 주황 · 검정). 에이전트 id로 고르게 나눠요 (가정 A46)
const LETTER_COLORS = ["#2B4ACB", "#FD5108", "#1A1C21", "#0F766E"];

function letterColorOf(key: string) {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return LETTER_COLORS[hash % LETTER_COLORS.length];
}

function Dash() {
  return <span className="text-[14px] leading-[22px] text-[#A1A8B3]">-</span>;
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-[6px]">
      {items.map((text, i) => (
        <li key={`${i}-${text}`} className="flex gap-2 text-[14px] leading-[22px] text-nv-text-primary">
          <span aria-hidden className="w-[6px] shrink-0 font-bold text-[#CBD1D6]">
            •
          </span>
          <span className="min-w-0 break-words">{text}</span>
        </li>
      ))}
    </ul>
  );
}

function MetricList({ items }: { items: CompareMetric[] }) {
  return (
    <div className="flex flex-col gap-[10px]">
      {items.map((m, i) =>
        m.before && m.after ? (
          <div key={`${i}-${m.name}`} className="flex flex-col gap-1">
            <span className="text-[13px] font-medium leading-4 text-[#6A6F77]">{m.name}</span>
            <div className="flex flex-wrap items-center gap-x-[6px]">
              <span className="text-[14px] leading-[17px] text-[#8E939A]">{m.before}</span>
              <NvArrowRightIcon size={14} className="shrink-0 text-[#A1A8B3]" />
              <span className="text-[15px] font-bold leading-[18px] text-nv-brand">{m.after}</span>
              {m.delta && <span className="text-[12px] font-medium leading-[14px] text-nv-brand">{m.delta}</span>}
            </div>
          </div>
        ) : (
          <p key={`${i}-${m.name}`} className="text-[14px] leading-[22px] text-nv-text-primary">
            {m.name} {m.after ?? m.delta ?? ""}
          </p>
        ),
      )}
    </div>
  );
}

function CellView({ kind, cell }: { kind: RowKind; cell: CompareCell | undefined }) {
  if (cell == null || (Array.isArray(cell) && cell.length === 0) || cell === "") return <Dash />;
  if (kind === "metrics" && Array.isArray(cell)) return <MetricList items={cell as CompareMetric[]} />;
  // 이전 서버 응답(쉼표로 이은 글)이면 한 줄로 그대로
  if (typeof cell === "string") return kind === "list" ? <Bullets items={[cell]} /> : <p className="text-[14px] leading-[22px] text-nv-text-primary">{cell}</p>;
  return <Bullets items={cell as string[]} />;
}

function ColumnHead({ card }: { card: FinderCard }) {
  return (
    <div className="flex min-w-0 flex-col gap-2 p-5">
      <div className="flex items-center gap-3">
        <span aria-hidden className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-nv-control bg-nv-bg-subtle">
          {card.logo_url ? (
            <img src={card.logo_url} alt="" className="h-full w-full object-contain" />
          ) : (
            <span className="text-[16px] font-bold leading-[19px]" style={{ color: letterColorOf(card.id) }}>
              {initialOf(card.name)}
            </span>
          )}
        </span>
        <div className="flex min-w-0 flex-col gap-[2px]">
          <div className="flex items-center gap-2">
            <span className="truncate text-[16px] font-bold leading-[22px] text-nv-text-primary" title={card.name}>
              {card.name}
            </span>
            {card.is_certified && <NvBadge className="shrink-0 py-0 leading-[19px]">PwC Certified</NvBadge>}
          </div>
          <span className="truncate text-[13px] leading-4 text-[#6A6F77]">{card.supplier_name}</span>
        </div>
      </div>
      <Link to={nvAgentDetailPath(card.id)} state={{ source: "my_page" }} className="w-fit text-[13px] font-bold leading-4 text-nv-text-primary hover:underline">
        상세 보기 ›
      </Link>
    </div>
  );
}

export interface MyCompareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // 고른 에이전트 (index = 비교함 목록 안 자리, rows[].cells의 자리와 같아요)
  columns: { card: FinderCard; index: number }[];
  rows: CompareList["rows"];
}

// 열 머리행(고정) + 세 묶음 본문. 좁은 화면에서는 열 폭(최소 240)을 지키고 바깥 상자가 가로로 스크롤해요
function CompareTable({ columns, rows }: Pick<MyCompareDialogProps, "columns" | "rows">) {
  const template = { gridTemplateColumns: `168px repeat(${Math.max(columns.length, 1)}, minmax(0, 1fr))` };
  return (
    <div style={{ minWidth: 168 + columns.length * 240 + 64 }}>
      <div className="sticky top-0 z-10 border-b-2 border-nv-text-primary bg-nv-surface px-8">
        <div className="grid" style={template}>
          <div className="p-5 text-[13px] font-medium leading-4 text-[#8E939A]">비교 항목</div>
          {columns.map(({ card }) => (
            <ColumnHead key={card.id} card={card} />
          ))}
        </div>
      </div>

      <div className="px-8 py-2">
        {GROUPS.map((group) => (
          <section key={group.title}>
            <h3 className="border-b border-nv-border px-5 py-3 text-[14px] font-bold leading-[17px] text-nv-text-primary">{group.title}</h3>
            {group.rows.map(({ key, kind }, rowIndex) => {
              const row = rows.find((r) => r.key === key);
              if (!row) return null;
              return (
                <div key={key} className={cn("grid border-b border-nv-bg-muted", rowIndex % 2 === 0 ? "bg-nv-surface" : "bg-[#FAFAFA]")} style={template}>
                  <div className="bg-[#F8F9FA] px-5 py-4 text-[14px] font-bold leading-[17px] text-nv-text-secondary">{row.label}</div>
                  {columns.map(({ card, index }) => (
                    <div key={card.id} className="min-w-0 px-5 py-4">
                      <CellView kind={kind} cell={row.cells?.[index] ?? row.values[index]} />
                    </div>
                  ))}
                </div>
              );
            })}
          </section>
        ))}
      </div>
    </div>
  );
}

export function MyCompareDialog({ open, onOpenChange, columns, rows }: MyCompareDialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[rgb(26_28_33_/_0.45)]" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[calc(100vh-80px)] w-[calc(100vw-80px)] max-w-[1360px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[16px] bg-nv-surface font-sans shadow-nv-popup focus:outline-none"
        >
          <div className="flex shrink-0 items-center justify-between border-b border-nv-border px-8 py-[22px]">
            <div className="flex items-center gap-3">
              <DialogPrimitive.Title className="text-[22px] font-bold leading-[26px] text-nv-text-primary">에이전트 비교</DialogPrimitive.Title>
              <span className="text-[14px] leading-[17px] text-[#6A6F77]">{columns.length}개 · 모든 항목 펼쳐 보기</span>
            </div>
            <DialogPrimitive.Close
              aria-label="닫기"
              className="flex h-9 w-9 items-center justify-center rounded-nv-chip text-nv-text-primary hover:bg-nv-bg-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand"
            >
              <NvLucideCloseIcon size={22} />
            </DialogPrimitive.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <CompareTable columns={columns} rows={rows} />
          </div>

          <div className="flex shrink-0 items-center justify-end border-t border-nv-border px-8 py-5">
            <DialogPrimitive.Close className="h-[42px] rounded-nv-control border border-nv-border-strong bg-nv-surface px-5 text-[15px] font-medium leading-[18px] text-nv-text-primary hover:bg-nv-bg-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand">
              닫기
            </DialogPrimitive.Close>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
